import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { INDUSTRY_PRESETS, SHOW_FIELDS, mergeQs } from "@/lib/invoiceSettings";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const Toggle = ({ label, checked, onChange, id }) => (
  <label className="flex items-center justify-between py-1.5 text-sm">
    <span>{label}</span><Switch checked={!!checked} onCheckedChange={onChange} data-testid={id} />
  </label>
);

function CustomFields({ qs, setQs }) {
  const [newLabel, setNewLabel] = useState("");
  const fields = qs.custom_fields;
  const set = (f) => setQs({ ...qs, custom_fields: f });
  function applyPreset(ind) {
    const have = new Set(fields.map((f) => f.label.toLowerCase()));
    const add = INDUSTRY_PRESETS[ind].fields.filter((l) => !have.has(l.toLowerCase())).map((label) => ({ label, enabled: true }));
    setQs({ ...qs, industry: ind, custom_fields: [...fields, ...add] });
  }
  return (
    <div className="space-y-3">
      <div><Label>Industry preset</Label>
        <Select value={qs.industry} onValueChange={applyPreset}>
          <SelectTrigger className="mt-1" data-testid="qs-industry"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{Object.entries(INDUSTRY_PRESETS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-xs text-slate-500 mt-1">Choosing an industry adds its suggested fields below.</p></div>
      {fields.map((f, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`qs-custom-field-${i}`}>
          <Input value={f.label} onChange={(e) => set(fields.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="h-8" data-testid={`qs-custom-label-${i}`} />
          <Switch checked={f.enabled !== false} onCheckedChange={(v) => set(fields.map((x, j) => j === i ? { ...x, enabled: v } : x))} data-testid={`qs-custom-toggle-${i}`} />
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => set(fields.filter((_, j) => j !== i))} data-testid={`qs-custom-delete-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="New field name" className="h-8" data-testid="qs-custom-new" />
        <Button size="sm" variant="outline" onClick={() => { if (!newLabel.trim()) return; set([...fields, { label: newLabel.trim(), enabled: true }]); setNewLabel(""); }} data-testid="qs-custom-add"><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>
    </div>
  );
}

function BankTab({ qs, setQs }) {
  const b = qs.bank; const set = (k, v) => setQs({ ...qs, bank: { ...b, [k]: v } });
  return (
    <div className="grid grid-cols-2 gap-3">
      {[["bank_name", "Bank Name"], ["holder", "Account Holder"], ["account_no", "Account No."], ["ifsc", "IFSC"], ["upi_id", "UPI ID (for QR)"]].map(([k, l]) => (
        <div key={k}><Label>{l}</Label><Input value={b[k]} onChange={(e) => set(k, e.target.value)} className="mt-1" data-testid={`qs-bank-${k}`} /></div>
      ))}
      <div className="col-span-2"><Label>Signature</Label>
        <div className="flex items-center gap-3 mt-1">
          {b.signature && <img src={b.signature} alt="signature" className="h-12 border rounded bg-white p-1" />}
          <label className="text-sm text-[#1A2A5E] underline cursor-pointer">Upload
            <input type="file" accept="image/*" className="hidden" data-testid="qs-signature-upload" onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; if (f.size > 500000) { toast.error("Signature must be under 500KB"); return; } const r = new FileReader(); r.onload = () => set("signature", r.result); r.readAsDataURL(f); }} />
          </label>
          {b.signature && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => set("signature", "")}>Remove</Button>}
        </div>
        <p className="text-xs text-slate-500 mt-1">Leave blank to use the details from Business Settings.</p></div>
    </div>
  );
}

export function QuickSettingsDialog({ open, onOpenChange, value, onSaved, canEdit }) {
  const [qs, setQs] = useState(mergeQs(value));
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setQs(mergeQs(value)); }, [open, value]);
  const num = (k, v) => setQs({ ...qs, [k]: Math.max(k === "default_due_days" ? 0 : 1, Number(v) || 0) });
  async function save() {
    setSaving(true);
    try { await api.put("/settings/detailed_invoice", qs); toast.success("Quick settings saved"); onSaved(qs); onOpenChange(false); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  }
  const preview = `${qs.prefix}${String(qs.next_number).padStart(qs.padding, "0")}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="qs-dialog">
        <DialogHeader><DialogTitle>Quick Settings</DialogTitle></DialogHeader>
        <Tabs defaultValue="numbering">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="numbering" data-testid="qs-tab-numbering">Numbering</TabsTrigger>
            <TabsTrigger value="fields" data-testid="qs-tab-fields">Fields</TabsTrigger>
            <TabsTrigger value="party" data-testid="qs-tab-party">Party & Items</TabsTrigger>
            <TabsTrigger value="bank" data-testid="qs-tab-bank">Bank & Sign</TabsTrigger>
          </TabsList>
          <TabsContent value="numbering" className="space-y-3 pt-3">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Prefix</Label><Input value={qs.prefix} onChange={(e) => setQs({ ...qs, prefix: e.target.value })} className="mt-1" data-testid="qs-prefix" /></div>
              <div><Label>Start From</Label><Input type="number" value={qs.next_number} onChange={(e) => num("next_number", e.target.value)} className="mt-1" data-testid="qs-next-number" /></div>
              <div><Label>Digits</Label><Input type="number" value={qs.padding} onChange={(e) => num("padding", e.target.value)} className="mt-1" data-testid="qs-padding" /></div>
            </div>
            <div className="text-sm text-slate-600">Next invoice: <span className="font-mono font-semibold text-[#1A2A5E]" data-testid="qs-number-preview">{preview}</span> (separate from POS bills; numbers already used are skipped)</div>
            <div className="grid grid-cols-3 gap-3 border-t pt-3">
              <div><Label>Quotation Prefix</Label><Input value={qs.quote_prefix} onChange={(e) => setQs({ ...qs, quote_prefix: e.target.value })} className="mt-1" data-testid="qs-quote-prefix" /></div>
              <div><Label>Start From</Label><Input type="number" value={qs.quote_next} onChange={(e) => num("quote_next", e.target.value)} className="mt-1" data-testid="qs-quote-next" /></div>
              <div><Label>Digits</Label><Input type="number" value={qs.quote_padding} onChange={(e) => num("quote_padding", e.target.value)} className="mt-1" data-testid="qs-quote-padding" /></div>
            </div>
            <div className="text-sm text-slate-600">Next quotation: <span className="font-mono font-semibold text-orange-600" data-testid="qs-quote-preview">{`${qs.quote_prefix}${String(qs.quote_next).padStart(qs.quote_padding, "0")}`}</span> (separate from POS quotations)</div>
            <div className="grid grid-cols-2 gap-3 border-t pt-3">
              <div><Label>Default payment terms (days)</Label><Input type="number" value={qs.default_due_days} onChange={(e) => num("default_due_days", e.target.value)} className="mt-1" data-testid="qs-due-days" /></div>
              <div className="pt-6"><Toggle label="Auto round off by default" checked={qs.auto_round_off} onChange={(v) => setQs({ ...qs, auto_round_off: v })} id="qs-round-off" /></div>
            </div>
          </TabsContent>
          <TabsContent value="fields" className="pt-3 grid md:grid-cols-2 gap-6">
            <div><div className="text-xs uppercase text-slate-400 mb-1">Show / hide</div>
              {SHOW_FIELDS.map(([k, l]) => <Toggle key={k} label={l} checked={qs.show[k]} onChange={(v) => setQs({ ...qs, show: { ...qs.show, [k]: v } })} id={`qs-show-${k}`} />)}</div>
            <div><div className="text-xs uppercase text-slate-400 mb-1">Custom fields</div><CustomFields qs={qs} setQs={setQs} /></div>
          </TabsContent>
          <TabsContent value="party" className="pt-3 grid md:grid-cols-2 gap-6">
            <div><div className="text-xs uppercase text-slate-400 mb-1">Party details on print</div>
              {[["phone", "Contact No."], ["address", "Billing Address"], ["gstin", "Party GSTIN"], ["ship_to", "Shipping Address"]].map(([k, l]) => <Toggle key={k} label={l} checked={qs.party[k]} onChange={(v) => setQs({ ...qs, party: { ...qs.party, [k]: v } })} id={`qs-party-${k}`} />)}</div>
            <div><div className="text-xs uppercase text-slate-400 mb-1">Item table columns</div>
              {[["hsn", "HSN / SAC"], ["discount", "Discount"], ["tax", "Tax %"]].map(([k, l]) => <Toggle key={k} label={l} checked={qs.items[k]} onChange={(v) => setQs({ ...qs, items: { ...qs.items, [k]: v } })} id={`qs-item-${k}`} />)}</div>
          </TabsContent>
          <TabsContent value="bank" className="pt-3"><BankTab qs={qs} setQs={setQs} /></TabsContent>
        </Tabs>
        <DialogFooter>
          {!canEdit && <span className="text-xs text-rose-600 mr-auto">Only users with Settings permission can save.</span>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#1A2A5E]" onClick={save} disabled={saving || !canEdit} data-testid="qs-save">{saving ? "Saving..." : "Save Settings"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
